/**
 * Admin-controlled feature switches.
 *
 * The server is the single source of truth. Until the first successful read the
 * optional capabilities stay off. Display switches retain the legacy visible
 * default when an older server does not publish them.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { apiClient } from './api/client';
import type { ImageGenerationPolicy } from './types';
import { AUTOMATION_PROMPT_DEFAULTS, automationPromptsSchema } from '../../server/src/modules/operations/automationPrompts.js';
import type { AutomationPrompts } from '../../server/src/modules/operations/automationPrompts.js';

export interface MediaInitiativeConfig {
  enabled: boolean;
  prompts: Record<ImageGenerationPolicy, string>;
}

export function createDisabledMediaInitiativeConfig(): MediaInitiativeConfig {
  return { enabled: false, prompts: { user_priority: '', character_priority: '', balanced: '' } };
}

export interface FeatureFlags {
  autoMemory: boolean;
  showContextInjection: boolean;
  showReasoning: boolean;
  imageInitiative: MediaInitiativeConfig;
  ttsInitiative: MediaInitiativeConfig;
  automationPrompts: AutomationPrompts;
}

export type AutomationFeatureFlags = Pick<FeatureFlags, 'imageInitiative' | 'ttsInitiative'>
  & Partial<Pick<FeatureFlags, 'automationPrompts'>>;

const DEFAULTS: FeatureFlags = {
  autoMemory: false, showContextInjection: true, showReasoning: true,
  imageInitiative: createDisabledMediaInitiativeConfig(),
  ttsInitiative: createDisabledMediaInitiativeConfig(),
  automationPrompts: { ...AUTOMATION_PROMPT_DEFAULTS },
};
const CACHE_MS = 60_000;
const LOAD_TIMEOUT_MS = 5000;

let current: FeatureFlags = DEFAULTS;
let loadedAt = 0;
let pending: Promise<FeatureFlags> | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function parseMediaInitiative(value: unknown): MediaInitiativeConfig {
  if (value === null || typeof value !== 'object') return createDisabledMediaInitiativeConfig();
  const config = value as Record<string, unknown>;
  const prompts = config.prompts;
  if (config.enabled !== true || prompts === null || typeof prompts !== 'object') {
    return createDisabledMediaInitiativeConfig();
  }
  const modes = prompts as Record<string, unknown>;
  if (typeof modes.user_priority !== 'string' || typeof modes.character_priority !== 'string' || typeof modes.balanced !== 'string') {
    return createDisabledMediaInitiativeConfig();
  }
  return {
    enabled: true,
    prompts: { user_priority: modes.user_priority, character_priority: modes.character_priority, balanced: modes.balanced },
  };
}

export function getFeatureFlags(): FeatureFlags {
  return current;
}

export function subscribeFeatureFlags(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Test seam: lets component tests exercise both the enabled and hidden layouts. */
export function setFeatureFlagsForTests(flags: Partial<FeatureFlags>): void {
  current = { ...current, ...flags };
  loadedAt = Date.now();
  emit();
}

export async function loadFeatureFlags(force = false): Promise<FeatureFlags> {
  if (!force && loadedAt > 0 && Date.now() - loadedAt < CACHE_MS) return current;
  if (pending !== null) return pending;
  pending = (async () => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
      const value = await Promise.race([
        apiClient.get<{
          autoMemory?: unknown; showContextInjection?: unknown; showReasoning?: unknown;
          imageInitiative?: unknown; ttsInitiative?: unknown; automationPrompts?: unknown;
        }>('/v1/features'),
        new Promise<never>((_, reject) => {
          timeout = setTimeout(() => reject(new Error('Feature configuration request timed out')), LOAD_TIMEOUT_MS);
        }),
      ]);
      const prompts = automationPromptsSchema.safeParse(value?.automationPrompts);
      current = {
        autoMemory: value?.autoMemory === true,
        showContextInjection: value?.showContextInjection === undefined || value.showContextInjection === true,
        showReasoning: value?.showReasoning === undefined || value.showReasoning === true,
        imageInitiative: parseMediaInitiative(value?.imageInitiative),
        ttsInitiative: parseMediaInitiative(value?.ttsInitiative),
        automationPrompts: prompts.success ? prompts.data : { ...AUTOMATION_PROMPT_DEFAULTS },
      };
    } catch {
      current = {
        ...current, autoMemory: false,
        imageInitiative: createDisabledMediaInitiativeConfig(),
        ttsInitiative: createDisabledMediaInitiativeConfig(),
      };
    } finally {
      if (timeout !== undefined) clearTimeout(timeout);
      loadedAt = Date.now();
      pending = null;
      emit();
    }
    return current;
  })();
  return pending;
}

export function useFeatureFlags(): FeatureFlags {
  const flags = useSyncExternalStore(subscribeFeatureFlags, getFeatureFlags, getFeatureFlags);
  useEffect(() => {
    void loadFeatureFlags();
    const refresh = (): void => { void loadFeatureFlags(true); };
    const timer = window.setInterval(refresh, CACHE_MS);
    window.addEventListener('focus', refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
    };
  }, []);
  return flags;
}
